import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { NgbCalendar, NgbDate, NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap/datepicker/ngb-date-struct';
import { DatepickerComponent } from '../../../components/inputs/datepicker/datepicker.component';
import { ActivatedRoute } from '@angular/router';
import { FormControl } from '@angular/forms';
import {
  formatDateLong,
  formatNgbDateShort,
  ngbDateToDate,
} from '../util/date-formatting.util';
import {
  SEARCH_DATE_DROPDOWN_DATE_RANGE,
  SearchDateDropdownType,
} from '../../../types/search-date-dropdown.type';
import { isBefore, isSameDay } from 'date-fns';

@Component({
  selector: 'app-search-date-dropdown',
  templateUrl: './search-date-dropdown.component.html',
  styleUrls: ['./search-date-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchDateDropdownComponent implements AfterViewInit {
  @Input()
  isMobile: boolean = false;

  hoveredDate: NgbDateStruct | null = null;
  today: NgbDate = this.calendar.getToday();
  fromDate: NgbDateStruct | null = null;
  toDate: NgbDateStruct | null = null;
  active: SearchDateDropdownType = SEARCH_DATE_DROPDOWN_DATE_RANGE.CUSTOM;
  focusOn: 'from' | 'to' | null = null;
  isOpen: boolean = false;

  @ViewChild('dp', { static: true })
  datepicker: DatepickerComponent | null = null;

  @ViewChild('dateDropdown', { static: true })
  dropdown: NgbDropdown | null = null;

  @Output()
  dateSelection: EventEmitter<[NgbDateStruct | null, NgbDateStruct | null]> =
    new EventEmitter<[NgbDateStruct | null, NgbDateStruct | null]>();

  @Output()
  openChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(private calendar: NgbCalendar, private route: ActivatedRoute) {
    const paramFrom: string | undefined =
      this.route.snapshot.queryParams['dateFrom'];
    const paramTo: string | undefined =
      this.route.snapshot.queryParams['dateTo'];

    if (paramFrom) {
      const [year, month, day] = paramFrom.split('-').map(Number);
      this.fromDate = {
        year,
        month,
        day,
      };
    }

    if (paramTo) {
      const [year, month, day] = paramTo.split('-').map(Number);
      this.toDate = {
        year,
        month,
        day,
      };
    }

    this.dateSelection.emit([this.fromDate, this.toDate]);
  }

  public get selectedDateFromAndToControl(): FormControl {
    const value =
      !!this.formatFromDate || !!this.formatToDate
        ? this.formatFromDate + ' - ' + this.formatToDate
        : '';
    return new FormControl(value);
  }

  public get selectedDateFromControl(): FormControl {
    const control = new FormControl(this.formatFromDate);
    control.disable();
    return control;
  }

  public get selectedDateToControl(): FormControl {
    const control = new FormControl(this.formatToDate);
    control.disable();
    return control;
  }

  public get fromValue(): string {
    return this.selectedDateFromControl.value
      ? this.selectedDateFromControl.value
      : 'Any date';
  }

  public get toValue(): string {
    return this.selectedDateToControl.value
      ? this.selectedDateToControl.value
      : 'Any date';
  }

  ngAfterViewInit(): void {
    this.datepicker?.datepicker.navigateTo();
  }

  onDateSelection(date: NgbDate): void {
    const fromDate = ngbDateToDate(this.fromDate);
    const toDate = ngbDateToDate(this.toDate);
    const tmpDate = ngbDateToDate(date);

    /**
     * Restart date range select when:
     * - both dates are already selected
     * - to date is before from date
     */
    const restartDateSelect =
      fromDate && toDate && !isSameDay(fromDate, toDate);
    const startBeforeEnd = fromDate && tmpDate && isBefore(tmpDate, fromDate);
    if (restartDateSelect || startBeforeEnd) {
      this.focusOn = 'from';
    }

    if (this.focusOn === 'from') {
      this.fromDate = date;

      if (this.isOpen) {
        this.focusOn = 'to';
      }
    }

    if (this.focusOn === 'to') {
      if (date.before(this.fromDate)) {
        this.fromDate = date;
      } else {
        this.toDate = date;
      }
    }

    this.dateSelection.emit([this.fromDate, this.toDate]);
    this.active = SEARCH_DATE_DROPDOWN_DATE_RANGE.CUSTOM;
  }

  get formatToDate(): string {
    return (
      (this.isMobile
        ? formatNgbDateShort(this.toDate)
        : formatDateLong(this.toDate)) ?? ''
    );
  }

  get formatFromDate(): string {
    return (
      (this.isMobile
        ? formatNgbDateShort(this.fromDate)
        : formatDateLong(this.fromDate)) ?? ''
    );
  }

  isHovered(date: NgbDate): boolean {
    return !!(
      this.fromDate &&
      !this.toDate &&
      this.hoveredDate &&
      date.after(this.fromDate) &&
      date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate): boolean {
    return (
      !!this.toDate && date.after(this.fromDate) && date.before(this.toDate)
    );
  }

  isRange(date: NgbDate): boolean {
    return (
      date.equals(this.fromDate) ||
      (this.toDate && date.equals(this.toDate)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  // method to get all the rest days count of the current year
  getRestDaysCount(): number {
    const currentTime = new Date();
    const currentYear = currentTime.getFullYear();
    const currentMonth = currentTime.getMonth() + 1;
    const currentDay = currentTime.getDate();

    const monthsLeftArray = [...Array(12).keys()].flatMap((index) => {
      const monthNumber = index + 1;
      if (monthNumber >= currentMonth) {
        return monthNumber;
      }
      return [];
    });

    return (
      monthsLeftArray.reduce((acc, monthNumber) => {
        const daysInTheMonth = new Date(currentYear, monthNumber, 0).getDate();

        return acc + daysInTheMonth;
      }, 0) - currentDay
    );
  }

  onCalendarModeChange(mode: typeof this.active): void {
    this.fromDate = this.today;
    const currentWeekDay = this.calendar.getWeekday(this.today);
    const currentTime = new Date();
    const currentYear = currentTime.getFullYear();
    const currentMonth = currentTime.getMonth() + 1;
    const currentDay = currentTime.getDate();
    const daysInTheMonth = new Date(currentYear, currentMonth, 0).getDate();

    switch (mode) {
      case SEARCH_DATE_DROPDOWN_DATE_RANGE.CUSTOM:
        this.toDate = null;
        break;
      case SEARCH_DATE_DROPDOWN_DATE_RANGE.TODAY:
        this.toDate = this.today;
        break;
      case SEARCH_DATE_DROPDOWN_DATE_RANGE.WEEKEND:
        if (currentWeekDay >= 5) {
          this.fromDate = this.today;
        } else {
          this.fromDate = this.calendar.getNext(
            // this Friday
            this.today,
            'd',
            5 - currentWeekDay
          );
        }
        if (currentWeekDay === 7) {
          this.toDate = this.today;
        } else {
          this.toDate = this.calendar.getNext(
            // this Sunday
            this.today,
            'd',
            7 - currentWeekDay
          );
        }
        break;
      case SEARCH_DATE_DROPDOWN_DATE_RANGE.NEXT_WEEKEND:
        this.fromDate = this.calendar.getNext(
          // next Friday
          this.today,
          'd',
          12 - currentWeekDay
        );
        this.toDate = this.calendar.getNext(
          // next Sunday
          this.today,
          'd',
          14 - currentWeekDay
        );
        break;
      case SEARCH_DATE_DROPDOWN_DATE_RANGE.WEEK:
        this.toDate = this.calendar.getNext(
          this.today,
          'd',
          7 - currentWeekDay
        );
        break;
      case SEARCH_DATE_DROPDOWN_DATE_RANGE.NEXT_WEEK:
        this.fromDate = this.calendar.getNext(
          // next Monday
          this.today,
          'd',
          8 - currentWeekDay
        );
        this.toDate = this.calendar.getNext(
          this.today,
          'd',
          14 - currentWeekDay
        );
        break;
      case SEARCH_DATE_DROPDOWN_DATE_RANGE.MONTH:
        this.toDate = this.calendar.getNext(
          this.today,
          'd',
          daysInTheMonth - currentDay
        );
        break;
      case SEARCH_DATE_DROPDOWN_DATE_RANGE.YEAR:
        this.toDate = this.calendar.getNext(
          this.today,
          'd',
          this.getRestDaysCount()
        );
        break;
    }

    this.dateSelection.emit([this.fromDate, this.toDate]);
    this.datepicker?.datepicker.navigateTo(this.fromDate);
  }

  clearAll(open: boolean = true): void {
    this.toDate = null;
    this.fromDate = null;
    this.dateSelection.emit([this.fromDate, this.toDate]);
    this.focusOn = 'from';
    this.active = SEARCH_DATE_DROPDOWN_DATE_RANGE.CUSTOM;
    if (open) {
      this.dropdown?.open();
    }
  }

  onFromDateFocus(event: MouseEvent, dateDropdown: NgbDropdown): void {
    if (this.isMobile) {
      event.preventDefault();
      event.stopPropagation();
    }

    dateDropdown.open();
    this.focusOn = 'from';
  }

  onToDateFocus(event: Event, dateDropdown: NgbDropdown): void {
    if (this.isMobile) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.toDate === null && this.fromDate === null) {
      this.fromDate = this.today;
    }

    if (this.focusOn !== 'to') {
      this.focusOn = 'to';
    }
    dateDropdown.open();
  }

  onDateDropdownOpenChange(change: boolean): void {
    if (!change) {
      this.focusOn = null;
    }

    this.isOpen = change;
    this.openChange.emit(change);
  }

  onClearClick(): void {
    this.toDate = null;
    this.fromDate = null;
    this.focusOn = 'from';
    this.dateSelection.emit([this.fromDate, this.toDate]);
  }

  protected readonly SEARCH_DATE_DROPDOWN_DATE_RANGE =
    SEARCH_DATE_DROPDOWN_DATE_RANGE;
}
